import React, { useState, useEffect } from "react";
import styled from "styled-components";
import supabase from "../supabase";
import { useNavigate } from "react-router-dom";

const PageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 20px;
`;

const Card = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 600px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  padding: 32px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 14px;
  color: #111827;
  outline: none;
  &:focus {
    border-color: #00876e;
    box-shadow: 0 0 0 2px rgba(0, 135, 110, 0.2);
  }
`;

const AvatarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
`;

const AvatarPreview = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #111827;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  overflow: hidden;
  border: 2px solid #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UploadButton = styled.label`
  background-color: #f3f4f6;
  color: #374151;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #d1d5db;
  transition: background 0.2s;

  &:hover {
    background-color: #e5e7eb;
  }

  input {
    display: none;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-top: 6px;

  &:hover {
    text-decoration: underline;
  }
`;

const SaveButton = styled.button`
  background-color: #00876e;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #00705a;
  }
`;

const ErrorMsg = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-bottom: 16px;
  background: #fef2f2;
  padding: 8px;
  border-radius: 6px;
`;

const SuccessMsg = styled.div`
  color: #065f46;
  font-size: 14px;
  margin-bottom: 16px;
  background: #d1fae5;
  padding: 8px;
  border-radius: 6px;
`;

export default function EditProfile({ profile }) {
  const [name, setName] = useState(profile?.name || "");
  const [email, setEmail] = useState(profile?.email || "");
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || "");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Keep state in sync if profile loads late
  useEffect(() => {
    if (profile) {
      setName(profile.name || "");
      setEmail(profile.email || "");
      setAvatarUrl(profile.avatar_url || "");
    }
  }, [profile]);

  const getInitials = (n) => {
    return (n || "").trim().charAt(0).toUpperCase() || "U";
  };

  const handleRemovePhoto = () => {
    setAvatarUrl("");
  };

  const handleFileChange = async (e) => {
    try {
      setLoading(true);
      setError(null);
      const file = e.target.files[0];
      if (!file) return;

      const fileExt = file.name.split(".").pop();
      const fileName = `${profile.id}-${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to Supabase Storage bucket "avatars"
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get Public URL
      const { data } = supabase.storage.from("avatars").getPublicUrl(filePath);

      setAvatarUrl(data.publicUrl);
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload image. Ensure an 'avatars' bucket exists.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setMessage(null);

      const emailChanged = email !== profile.email;

      // 1. If the email changed, update the Auth login credential first.
      // This only sends a confirmation link — the login email doesn't
      // actually change until the user clicks it — so don't touch the
      // Users table's email if this fails.
      if (emailChanged) {
        const { error: emailError } = await supabase.auth.updateUser({
          email,
        });
        if (emailError) {
          setError(`Failed to update email: ${emailError.message}`);
          setLoading(false);
          return;
        }
      }

      // 2. Update Users table
      const { error: updateError } = await supabase
        .from("Users")
        .update({
          name,
          email,
          avatar_url: avatarUrl,
        })
        .eq("id", profile.id);

      if (updateError) throw updateError;

      setMessage(
        emailChanged
          ? "Profile updated. We sent a confirmation link to your new email address — click it before you can log in with it. Keep using your old email to log in until then."
          : "Profile updated successfully! Refresh to see changes."
      );

      // Optional: Force reload to refresh context/navbar if context isn't live
      // window.location.reload();
    } catch (err) {
      console.error("Update error:", err);
      setError("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <Card>
        <Title>Edit Profile</Title>

        {error && <ErrorMsg>{error}</ErrorMsg>}
        {message && <SuccessMsg>{message}</SuccessMsg>}

        <AvatarSection>
          <AvatarPreview>
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" />
            ) : (
              getInitials(name)
            )}
          </AvatarPreview>
          <div>
            <UploadButton>
              {loading ? "Uploading..." : "Upload New Picture"}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={loading}
              />
            </UploadButton>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              Recommended: Square JPG or PNG
            </div>
            {avatarUrl && (
              <RemoveButton type="button" onClick={handleRemovePhoto}>
                Remove Photo
              </RemoveButton>
            )}
          </div>
        </AvatarSection>

        <FormGroup>
          <Label>Full Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
          />
        </FormGroup>

        <FormGroup>
          <Label>Email Address</Label>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
          />
        </FormGroup>

        <SaveButton onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save Changes"}
        </SaveButton>
      </Card>
    </PageWrapper>
  );
}
